import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Typography,
  Stack,
  Container,
  Link,
  CircularProgress
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { showAlertError } from '@utils/Alerts/Error';
import { useNavigate } from 'react-router-dom';

import DealersService from '@api/Dealers/DealersCtrl';

const validationSchema = Yup.object().shape({
  userOrEmail: Yup.string().required('E-mail or Username is required'),
  password: Yup.string().required('Password is required')
});

const Login = () => {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const [loading, setLoading] = useState(false);
  const [tokenExists, setTokenExists] = useState(false);
  const [user, setUser] = useState<{ name:string; dealerName: string; email: string } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    setTokenExists(!!token);
    const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      setUser(JSON.parse(storedUser));
    } catch {
      setUser(null);
    }
  }
  }, []);

  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      const user = await DealersService.login(data.userOrEmail, data.password);
      if (user) {
        // Guardar user en localStorage
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', 'true'); // marca simple de login
        navigate('/spaCoverSummary');
      } else {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        showAlertError('Wrong credentials');
      }
    } catch (error: any) {
      const message = error?.response?.data?.error || 'There was an issue';
      showAlertError(message);
    } finally {
      setLoading(false);
    }
  };
  

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setTokenExists(false);
    setUser(null);
    navigate('/login');
  };

  if (tokenExists && user) {
    return (
      <Container maxWidth="sm" sx={{ my: 25, textAlign: 'center' }}>
      <Typography variant="h5" gutterBottom>
        You are already logged in.
      </Typography>
      <Typography variant="body1">
        {user.dealerName || user.name} ({user.email})
      </Typography>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleLogout}
        sx={{ mt: 2 }}
      >
        Logout
      </Button>
    </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Stack spacing={4} mt={20} mb={8}>
        <Typography variant="h5" align="center">
          Login
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={3}>
            <Controller
              name="userOrEmail"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="E-mail or Username"
                  fullWidth
                  error={!!errors.userOrEmail}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Password"
                  type="password"
                  fullWidth
                  error={!!errors.password}
                />
              )}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Logging in...' : 'Enter'}
            </Button>
          </Stack>
        </form>

        <Stack alignItems="center">
          <Typography variant="body2">Don't have an account yet?</Typography>
          <Link href="mailto:sc1@pspfab.com" variant="body2" underline="hover">
            Contact Us
          </Link>
        </Stack>
      </Stack>
    </Container>
  );
};

export default Login;
